import { useCallback, useEffect, useState } from "react";

import { useAuth, getSignedInHint, getSignedInHintId } from "../../../shared/auth";
import { syncAccountNameToLocal } from "../../../shared/profile";
import {
  getLocalPlayer, saveLocalPlayer, loadLocalPlayer, subscribeLocalPlayers, loadLobbyPlayerCount, saveLobbyPlayerCount,
  removeLobbyPlayer, MAX_LOBBY_PLAYERS, type LocalPlayerPrefs,
} from "../../../shared/local-players";
import { keyPairs } from "../../../shared/key-util.js";
import { WORM_COLOR_IDS } from "../../../shared/worm-colors";
import { fetchRankedRatings, type RankedMode, type RankedRatingRow } from "../../../shared/rating/store";
import { subscribeLobbyCounts, subscribeCustomLobbies, peekLobbyCounts, peekCustomLobbies, type LobbyCount, type CustomLobby } from "../../../online/net";
import { randomPlayerName } from "../lobby";
import { FORCE_LAND } from "../../../shared/design-flags";
import { ON_PORTAL } from "../../../shared/portal-env";

// ---------- landscape phone ----------
// The one test for "this is a phone held sideways": the start page is then the
// game app (no website navbar), everywhere else it is the PC page. (A game
// site's build is the game app everywhere: shared/portal-env.ts.)
const LANDSCAPE_PHONE = "(pointer: coarse) and (orientation: landscape) and (max-height: 500px)";
export function useLandscapePhone(): boolean {
  const [on, setOn] = useState(() => FORCE_LAND || ON_PORTAL || (typeof matchMedia === "function" && matchMedia(LANDSCAPE_PHONE).matches));
  useEffect(() => {
    if (FORCE_LAND || ON_PORTAL || typeof matchMedia !== "function") return;
    const mq = matchMedia(LANDSCAPE_PHONE);
    const update = () => setOn(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return on;
}

// ---------- the players on this device ----------
// The colours a player picks from: the first dozen of the palette (core's own
// colours come first in it).
export const PICK_COLORS: string[] = WORM_COLOR_IDS.slice(0, 12);
const NAME_MAX = 16; // as the old lobby's name inputs

export interface RosterPlayer extends LocalPlayerPrefs { slot: number }

function readRoster(): RosterPlayer[] {
  const count = loadLobbyPlayerCount();
  const out: RosterPlayer[] = [];
  for (let i = 0; i < count; i++) out.push({ slot: i, ...getLocalPlayer(i, i === 0 ? "Guest" : "Player " + (i + 1)) });
  return out;
}

// The local roster (you, plus anyone sharing this PC), read from and written to
// the shared store (shared/local-players), which is what a match seats from.
export function useLocalRoster() {
  const { user } = useAuth();
  const [players, setPlayers] = useState<RosterPlayer[]>(() => {
    // A first visit gets a handle of its own, saved so it stays the same.
    if (!loadLocalPlayer(0)) saveLocalPlayer(0, getLocalPlayer(0, randomPlayerName()));
    return readRoster();
  });
  const refresh = useCallback(() => setPlayers(readRoster()), []);

  // Other screens write the store too (the shop's equips, an account-settings
  // download, the lobby setup). Deferred a tick: some of them write while
  // rendering, and a state change here must not land inside their render.
  useEffect(() => {
    let live = true;
    const off = subscribeLocalPlayers(() => { queueMicrotask(() => { if (live) refresh(); }); });
    return () => { live = false; off(); };
  }, [refresh]);

  // A newly signed-in account's name becomes player 1's name, once per account
  // (later edits are kept): the rule the start page has always had.
  useEffect(() => {
    if (!user) return;
    syncAccountNameToLocal(user);
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Only what changed is written, plus the name / colour / keys the store needs:
  // the flag and the equipped cosmetics are left to the store, which keeps
  // what it has (a read here is gated while signed out and would wipe them).
  const patch = useCallback((slot: number, change: Partial<LocalPlayerPrefs>) => {
    const cur = getLocalPlayer(slot, "Player " + (slot + 1));
    saveLocalPlayer(slot, { name: cur.name, colorId: cur.colorId, left: cur.left, right: cur.right, ...change });
    refresh();
  }, [refresh]);

  const setName = useCallback((slot: number, name: string) => patch(slot, { name: name.substring(0, NAME_MAX) }), [patch]);
  const setColor = useCallback((slot: number, colorId: string) => patch(slot, { colorId }), [patch]);
  const setFlag = useCallback((slot: number, flag: string) => patch(slot, { flag }), [patch]);

  // A key belongs to one player and one side: picking it frees it wherever it
  // was held before.
  const setKey = useCallback((slot: number, side: "left" | "right", key: string) => {
    readRoster().forEach((p) => {
      let { left, right } = p;
      if (left === key) left = null;
      if (right === key) right = null;
      if (p.slot === slot) { if (side === "left") left = key; else right = key; }
      if (left !== p.left || right !== p.right) saveLocalPlayer(p.slot, { name: p.name, colorId: p.colorId, left, right });
    });
    refresh();
  }, [refresh]);

  const addPlayer = useCallback(() => {
    const cur = readRoster();
    if (cur.length >= MAX_LOBBY_PLAYERS) return;
    const slot = cur.length;
    const p = getLocalPlayer(slot, "Player " + (slot + 1));
    // The stored (or default) colour and keys may be taken by now: give the new
    // player the first free ones.
    const usedColors = cur.map((x) => x.colorId);
    const colorId = usedColors.includes(p.colorId) ? PICK_COLORS.find((c) => !usedColors.includes(c)) || p.colorId : p.colorId;
    const usedKeys = new Set<string>();
    cur.forEach((x) => { if (x.left) usedKeys.add(x.left); if (x.right) usedKeys.add(x.right); });
    let { left, right } = p;
    if (!left || !right || usedKeys.has(left) || usedKeys.has(right)) {
      const free = (keyPairs as Array<{ left: string; right: string }>).find((k) => !usedKeys.has(k.left) && !usedKeys.has(k.right));
      left = free ? free.left : null;
      right = free ? free.right : null;
    }
    saveLobbyPlayerCount(slot + 1);
    // they sit at your PC: your flag, unless they had their own
    saveLocalPlayer(slot, { name: p.name, colorId, left, right, flag: loadLocalPlayer(slot)?.flag ?? cur[0].flag });
    refresh();
  }, [refresh]);

  const removePlayer = useCallback((slot: number) => { removeLobbyPlayer(slot); refresh(); }, [refresh]);

  return { players, setName, setColor, setFlag, setKey, addPlayer, removePlayer, max: MAX_LOBBY_PLAYERS };
}

// ---------- ranked ratings ----------
// Both ladders for the signed-in player: undefined while loading, null when
// signed out. Seeded on the first frame from the last read this device made for
// the last signed-in account, so a reload does not flash "Sign in".
export type Ratings = Record<RankedMode, RankedRatingRow | null> | null | undefined;

const RANKED_CACHE = "ar.rankedStrip.";
function readRankedCache(id: string): Ratings {
  try {
    const raw = localStorage.getItem(RANKED_CACHE + id);
    return raw ? JSON.parse(raw) : undefined;
  } catch {
    return undefined;
  }
}
function writeRankedCache(id: string, r: Ratings): void {
  try {
    localStorage.setItem(RANKED_CACHE + id, JSON.stringify(r));
  } catch {
    /* storage blocked: it just loads without a seed */
  }
}

// Dev / preview aid (?ranksdemo, as the old start page had): a Gold II duel
// ladder and an FFA ladder two placement matches in, without signing in.
const DEMO_RATINGS = { duel: { placed: true, displayRating: 1171, games: 31 }, ffa: { placed: false, displayRating: 1000, games: 2 } } as unknown as Ratings;
const ranksDemo = () => typeof window !== "undefined" && new URLSearchParams(window.location.search).has("ranksdemo");

export function useRankedRatings(): { ratings: Ratings; signedIn: boolean } {
  const { user, loading: authLoading } = useAuth();
  const [demo] = useState(ranksDemo);
  const [ratings, setRatings] = useState<Ratings>(() => {
    const id = getSignedInHintId();
    return id ? readRankedCache(id) : undefined;
  });
  useEffect(() => {
    if (!user) {
      if (!authLoading) setRatings(null);
      return;
    }
    const cached = readRankedCache(user.id);
    setRatings(cached);
    let live = true;
    fetchRankedRatings(user.id).then((r) => {
      if (!live) return;
      // a failed read keeps what the cache showed rather than blanking it
      if (r) writeRankedCache(user.id, r);
      setRatings(r ?? cached ?? { duel: null, ffa: null });
    });
    return () => { live = false; };
  }, [user?.id, authLoading]);
  // Until the session resolves, trust the hint: a signed-in reload never shows
  // the signed-out state.
  if (demo) return { ratings: DEMO_RATINGS, signedIn: true };
  return { ratings, signedIn: !!user || (authLoading && getSignedInHint()) };
}

// ---------- the Royale's clock ----------
// A Royale starts on every half hour of the clock (the server's schedule, see
// apps/edge-server/src/directors/royale.js). Until the server has said when,
// the clock counts down to the next half hour, so it never sits at "-:--".
const ROYALE_EVERY_MS = 30 * 60 * 1000;
const nextHalfHour = (now: number) => Math.ceil(now / ROYALE_EVERY_MS) * ROYALE_EVERY_MS;

export interface RoyaleNow {
  count: LobbyCount | null; // null until the server's first push
  secs: number | null;      // to the start (the next half hour until the server says); 0 once it has passed, until the next push
  pct: number;              // players signed up, of the capacity (0..100)
}
export function useRoyale(): RoyaleNow {
  // (the last counts the Lobby sent, so a start page you come back to draws at once)
  const [count, setCount] = useState<LobbyCount | null>(() => peekLobbyCounts()?.royale || null);
  const [, tick] = useState(0);
  useEffect(() => subscribeLobbyCounts((c) => setCount(c.royale || null)), []);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const startAt = count && count.startAt > 0 ? count.startAt : nextHalfHour(Date.now());
  const secs = Math.max(0, Math.ceil((startAt - Date.now()) / 1000));
  const pct = count ? Math.max(0, Math.min(100, (count.players / Math.max(1, count.capacity)) * 100)) : 0;
  return { count, secs, pct };
}
export const mmss = (t: number | null) => (t == null ? "-:--" : `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`);

// ---------- open public rooms ----------
export function useOpenRooms(): CustomLobby[] {
  const [rooms, setRooms] = useState<CustomLobby[]>(() => peekCustomLobbies() || []);
  useEffect(() => subscribeCustomLobbies((l) => setRooms(l)), []);
  return rooms;
}

// ---------- coming back to the panel you left from ----------
// A page opened from the phone's menu or player card (News, Shop, Profile...)
// returns to that panel, not to a bare start page, whether you come back with
// the phone's back button or the page's own. Set on the way out, read by the
// next start page and then cleared (setReturnSheet(null)).
let returnSheet: string | null = null;
export function setReturnSheet(sheet: string | null): void { returnSheet = sheet; }
export function peekReturnSheet(): string | null { return returnSheet; }
